import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { ImageViewer, type ViewerImage } from '@/components/image-viewer'
import { Download, AlertCircle, Paperclip, Eye, Image as ImageIcon } from 'lucide-react'
import { useMessageAttachments } from '@/features/communications/hooks'
import { useThreadMailbox, type ThreadMailbox } from '@/features/communications/mailbox'
import { useAuth } from '@/hooks/use-auth'
import { formatFileSize } from './formatting'
import { SanitizedEmailHtml } from '@/components/sanitized-email-html'
import { getApiErrorMessage } from '@/lib/api-error'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

// Graph's inline attachment endpoint caps each attachment at ~3 MB and the
// whole message at ~30 MB; mirror those limits client-side so users see the
// problem before round-tripping through the backend.
export const MAX_REPLY_ATTACHMENT_BYTES = 3 * 1024 * 1024
export const MAX_REPLY_ATTACHMENTS_TOTAL_BYTES = 30 * 1024 * 1024

function isViewableInline(contentType: string): boolean {
  // Active documents such as SVG remain download-only, never app-origin browser tabs.
  return (
    contentType === 'application/pdf' ||
    /^image\/(png|jpeg|gif|webp|avif|bmp|x-icon|vnd\.microsoft\.icon)$/.test(contentType)
  )
}

function inlineImageContentIds(html: string): Set<string> {
  const body = new DOMParser().parseFromString(html, 'text/html')
  return new Set(
    Array.from(body.querySelectorAll('img'))
      .map((image) => image.getAttribute('src') ?? '')
      .filter((source) => source.toLowerCase().startsWith('cid:'))
      .map((source) => source.slice(4)),
  )
}

/** Where a message's attachments are served: supplier and customer threads have their own routes. */
const ATTACHMENT_ROOTS: Record<ThreadMailbox, string> = {
  supplier: '/dashboard/communications/messages/',
  customer: '/dashboard/customer-communications/messages/',
}

async function fetchAttachmentBlob(
  messageUuid: string,
  attachmentId: string,
  getAccessToken: () => Promise<string | null>,
  download: boolean,
  mailbox: ThreadMailbox,
): Promise<Blob> {
  const token = await getAccessToken()
  if (!token) throw new Error('Not authenticated')
  const url =
    `${API_BASE_URL}${ATTACHMENT_ROOTS[mailbox]}${encodeURIComponent(messageUuid)}` +
    `/attachments/${encodeURIComponent(attachmentId)}${download ? '?download=true' : ''}`
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!resp.ok) {
    throw new Error(`Failed to fetch attachment (${resp.status})`)
  }
  return resp.blob()
}

/** Save bytes already in the browser under the attachment's own name. */
function saveObjectUrl(url: string, name: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
}

async function downloadAttachment(
  messageUuid: string,
  attachmentId: string,
  name: string,
  getAccessToken: () => Promise<string | null>,
  mailbox: ThreadMailbox,
) {
  const blob = await fetchAttachmentBlob(messageUuid, attachmentId, getAccessToken, true, mailbox)
  const url = URL.createObjectURL(blob)
  saveObjectUrl(url, name)
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

const isPicture = (contentType: string) =>
  contentType.startsWith('image/') && isViewableInline(contentType)

// Microsoft Graph serves about four requests at a time per mailbox. Embedded images from every
// email on the page share three of them, so opening a long thread is not throttled (USA-1484).
const MAX_EMBEDDED_IMAGE_LOADS = 3
let embeddedImageLoads = 0
const waitingEmbeddedImageLoads: Array<() => void> = []

async function withEmbeddedImageSlot<T>(load: () => Promise<T>): Promise<T> {
  if (embeddedImageLoads < MAX_EMBEDDED_IMAGE_LOADS) embeddedImageLoads++
  else await new Promise<void>((resolve) => waitingEmbeddedImageLoads.push(resolve))
  try {
    return await load()
  } finally {
    const next = waitingEmbeddedImageLoads.shift()
    if (next) next()
    else embeddedImageLoads--
  }
}

export function MessageAttachments({
  messageUuid,
  hasAttachments,
}: {
  messageUuid: string | undefined
  hasAttachments: boolean
}) {
  const { getAccessToken } = useAuth()
  const mailbox = useThreadMailbox()
  const {
    data: attachments,
    isLoading,
    isError,
  } = useMessageAttachments(messageUuid, hasAttachments)
  const [busy, setBusy] = useState<string | null>(null)
  // A picture opened in the viewer; its object URL lives until the viewer closes.
  const [viewing, setViewing] = useState<(ViewerImage & { url: string }) | null>(null)
  useEffect(() => {
    if (!viewing) return
    return () => URL.revokeObjectURL(viewing.url)
  }, [viewing])

  if (!hasAttachments || !messageUuid) return null
  if (isLoading) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Paperclip className="h-3 w-3" /> Loading attachments...
      </div>
    )
  }
  if (isError) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
        <AlertCircle className="h-3 w-3" /> Failed to load attachments
      </div>
    )
  }
  // Embedded images show with the email body (MessageEmailBody).
  const visible = (attachments ?? []).filter((attachment) => !attachment.is_inline)
  if (visible.length === 0) return null

  const handleView = async (attachmentId: string, name: string) => {
    // Open during the click, before authentication/network awaits lose user activation.
    const win = window.open('about:blank', '_blank')
    if (!win) {
      toast.error('Allow pop-ups to view attachments, or use Download.')
      return
    }
    win.opener = null
    setBusy(`view:${attachmentId}`)
    try {
      const blob = await fetchAttachmentBlob(messageUuid, attachmentId, getAccessToken, false, mailbox)
      if (!isViewableInline(blob.type)) throw new Error('Use Download to open this file.')
      const url = URL.createObjectURL(blob)
      win.location.replace(url)
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      win.close()
      toast.error(`Could not open ${name}: ${getApiErrorMessage(e)}`)
    } finally {
      setBusy(null)
    }
  }

  // Pictures open in the viewer instead of a new tab (USA-1623).
  const handleViewPicture = async (attachmentId: string, name: string, size: number) => {
    setBusy(`view:${attachmentId}`)
    try {
      const blob = await fetchAttachmentBlob(messageUuid, attachmentId, getAccessToken, false, mailbox)
      if (!isPicture(blob.type)) throw new Error('Use Download to open this file.')
      setViewing({ key: attachmentId, name, detail: formatFileSize(size), url: URL.createObjectURL(blob) })
    } catch (e) {
      toast.error(`Could not open ${name}: ${getApiErrorMessage(e)}`)
    } finally {
      setBusy(null)
    }
  }

  const handleDownload = async (attachmentId: string, name: string) => {
    setBusy(`download:${attachmentId}`)
    try {
      await downloadAttachment(messageUuid, attachmentId, name, getAccessToken, mailbox)
    } catch (e) {
      toast.error(`Could not download ${name}: ${getApiErrorMessage(e)}`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mt-2">
      <details open className="group/details">
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
          Attachments ({visible.length})
        </summary>
        <ul className="mt-1.5 space-y-1.5">
          {visible.map((att) => {
            const viewable = isViewableInline(att.content_type)
            const sizeLabel = formatFileSize(att.size)
            const viewKey = `view:${att.id}`
            const dlKey = `download:${att.id}`
            return (
              <li key={att.id} className="rounded-md border bg-card p-2">
                <p className="break-all text-xs font-medium leading-snug" title={att.name}>
                  {att.name}
                </p>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <span className="min-w-0 text-xs text-muted-foreground">{sizeLabel}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    {viewable && (
                      <LoadingButton
                        size="icon-xs"
                        variant="ghost"
                        className="sm:w-auto sm:px-2"
                        loading={busy === viewKey}
                        onClick={() =>
                          isPicture(att.content_type)
                            ? handleViewPicture(att.id, att.name, att.size)
                            : handleView(att.id, att.name)
                        }
                        aria-label="View"
                        title="View"
                      >
                        <Eye />
                        <span className="sr-only text-xs sm:not-sr-only">View</span>
                      </LoadingButton>
                    )}
                    <LoadingButton
                      size="icon-xs"
                      variant="ghost"
                      className="sm:w-auto sm:px-2"
                      loading={busy === dlKey}
                      onClick={() => handleDownload(att.id, att.name)}
                      aria-label="Download"
                      title="Download"
                    >
                      <Download />
                      <span className="sr-only text-xs sm:not-sr-only">Download</span>
                    </LoadingButton>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </details>
      <ImageViewer
        images={viewing ? [viewing] : []}
        index={viewing ? 0 : null}
        onIndexChange={(index) => {
          if (index === null) setViewing(null)
        }}
        onDownload={(image) => saveObjectUrl(image.url!, image.name)}
      />
    </div>
  )
}

export function MessageEmailBody({
  html,
  messageUuid,
  showEmbeddedImages = false,
}: {
  html: string
  messageUuid?: string
  /** List the email's embedded images as thumbnails under the body. */
  showEmbeddedImages?: boolean
}) {
  const { getAccessToken } = useAuth()
  const mailbox = useThreadMailbox()
  const hasInlineImages = /\bcid:/i.test(html)
  const { data: attachments } = useMessageAttachments(messageUuid, hasInlineImages)
  const [images, setImages] = useState<{
    html: string
    urls: Record<string, string>
  } | null>(null)
  const [viewing, setViewing] = useState<number | null>(null)
  const contentIds = useMemo(
    () => (hasInlineImages ? inlineImageContentIds(html) : new Set<string>()),
    [hasInlineImages, html],
  )

  useEffect(() => {
    if (!messageUuid || !attachments || !hasInlineImages) return
    let cancelled = false
    const urls: Record<string, string> = Object.create(null)
    const loads = attachments.map(async (attachment) => {
      const contentId = attachment.content_id
      if (!contentId || !contentIds.has(contentId) || !isPicture(attachment.content_type)) return
      try {
        const blob = await withEmbeddedImageSlot(() =>
          cancelled
            ? Promise.resolve(null)
            : fetchAttachmentBlob(messageUuid, attachment.id, getAccessToken, false, mailbox),
        )
        if (blob && !cancelled && isPicture(blob.type)) {
          urls[contentId] = URL.createObjectURL(blob)
        }
      } catch {
        // Keep the email readable; the image's Download in the viewer allows retry.
      }
    })
    void Promise.all(loads).then(() => {
      if (!cancelled) setImages({ html, urls })
    })
    return () => {
      cancelled = true
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url))
    }
  }, [attachments, contentIds, getAccessToken, hasInlineImages, html, mailbox, messageUuid])

  const urls = images?.html === html ? images.urls : {}
  const embedded = messageUuid
    ? (attachments ?? []).filter(
        (attachment) =>
          attachment.is_inline && !!attachment.content_id && contentIds.has(attachment.content_id),
      )
    : []
  const viewerImages: ViewerImage[] = embedded.map((attachment) => ({
    key: attachment.id,
    name: attachment.name,
    detail: formatFileSize(attachment.size),
    url: urls[attachment.content_id!] ?? null,
  }))

  const download = async (image: ViewerImage) => {
    if (image.url) return saveObjectUrl(image.url, image.name)
    try {
      await downloadAttachment(messageUuid!, image.key, image.name, getAccessToken, mailbox)
    } catch (e) {
      toast.error(`Could not download ${image.name}: ${getApiErrorMessage(e)}`)
    }
  }

  return (
    <>
      <SanitizedEmailHtml
        html={html}
        inlineImages={urls}
        onImageClick={(contentId) => {
          const index = embedded.findIndex((attachment) => attachment.content_id === contentId)
          if (index >= 0) setViewing(index)
        }}
      />
      {showEmbeddedImages && embedded.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
            Embedded images ({embedded.length})
          </summary>
          <ul className="mt-2 flex flex-wrap gap-2">
            {viewerImages.map((image, index) => (
              <li key={image.key}>
                <button
                  type="button"
                  onClick={() => setViewing(index)}
                  aria-label={`View ${image.name}`}
                  title={`${image.name} · ${image.detail}`}
                  className="flex size-16 cursor-zoom-in items-center justify-center overflow-hidden rounded-md border bg-muted/40 p-1.5 transition-colors hover:border-foreground/30 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                >
                  {image.url ? (
                    <img src={image.url} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <ImageIcon className="size-5 text-muted-foreground" aria-hidden />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
      <ImageViewer
        images={viewerImages}
        index={viewing}
        onIndexChange={setViewing}
        onDownload={(image) => void download(image)}
      />
    </>
  )
}
